import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mail, Bell, Truck, ImagePlus, LayoutTemplate } from "lucide-react";
import { api, apiError } from "@/lib/api";

const SettingsTab = () => {
  const [contactEmail, setContactEmail] = useState("");
  const [threshold, setThreshold] = useState("");
  const [shipFlat, setShipFlat] = useState("");
  const [shipFree, setShipFree] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [heroImg, setHeroImg] = useState("");
  const [manifestoImg, setManifestoImg] = useState("");
  const [uploading, setUploading] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);
  const heroRef = useRef(null);
  const manifestoRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get("/admin/settings/store"), api.get("/settings/site")])
      .then(([store, site]) => {
        setContactEmail(store.data.contact_email || "");
        setThreshold(store.data.low_stock_threshold ?? "");
        setShipFlat(store.data.shipping_flat_rate ?? "");
        setShipFree(store.data.free_shipping_above ?? "");
        setLogoUrl(site.data.logo_url || "");
        setHeroImg(site.data.hero_image || "");
        setManifestoImg(site.data.manifesto_image || "");
      })
      .catch((e) => toast.error(apiError(e, "Could not load settings")))
      .finally(() => setLoading(false));
  }, []);

  const upload = async (file, key) => {
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      if (key === "logo") setLogoUrl(data.url);
      if (key === "hero") setHeroImg(data.url);
      if (key === "manifesto") setManifestoImg(data.url);
      toast.success("Image uploaded — press Save Settings to publish it");
    } catch (e) {
      toast.error(apiError(e, "Upload failed. Please try again."));
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const [store] = await Promise.all([
        api.put("/admin/settings/store", {
          contact_email: contactEmail,
          low_stock_threshold: threshold === "" ? null : Number(threshold),
          shipping_flat_rate: shipFlat === "" ? 0 : Number(shipFlat),
          free_shipping_above: shipFree === "" ? null : Number(shipFree),
        }),
        api.put("/admin/settings/site", {
          logo_url: logoUrl,
          hero_image: heroImg,
          manifesto_image: manifestoImg,
        }),
      ]);
      setContactEmail(store.data.contact_email);
      setThreshold(store.data.low_stock_threshold ?? "");
      setShipFlat(store.data.shipping_flat_rate ?? "");
      setShipFree(store.data.free_shipping_above ?? "");
      toast.success("Settings saved — live on the storefront now");
    } catch (e) {
      toast.error(apiError(e, "Could not save settings"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-cream/30">
        Loading…
      </p>
    );
  }

  const ImageSlot = ({ label, hint, value, slotKey, inputRef, testid }) => (
    <div className="border border-cream/10 p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60">{label}</p>
      <p className="mt-1 text-xs text-cream/35">{hint}</p>
      <div className="mt-3 flex items-center gap-4">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden border border-cream/15 bg-espresso">
          {value ? (
            <img src={value} alt={label} data-testid={`${testid}-preview`} className="h-full w-full object-contain" />
          ) : (
            <ImagePlus size={18} strokeWidth={1} className="text-gold-muted/40" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            data-testid={testid}
            onClick={() => inputRef.current?.click()}
            disabled={uploading === slotKey}
            className="inline-flex items-center gap-2 border border-gold/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-espresso disabled:opacity-50"
          >
            <ImagePlus size={12} /> {uploading === slotKey ? "Uploading…" : value ? "Change" : "Upload"}
          </button>
          {value && (
            <button
              type="button"
              data-testid={`${testid}-remove`}
              onClick={() => {
                if (slotKey === "logo") setLogoUrl("");
                if (slotKey === "hero") setHeroImg("");
                if (slotKey === "manifesto") setManifestoImg("");
              }}
              className="text-left font-mono text-[9px] uppercase tracking-[0.2em] text-cream/35 transition-colors hover:text-red-400"
            >
              Remove
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            upload(e.target.files?.[0], slotKey);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="mx-auto mt-10 max-w-2xl space-y-6" data-testid="settings-tab">
      <div className="border border-cream/10 bg-espresso-surface p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <LayoutTemplate size={18} strokeWidth={1.5} className="text-gold" />
          <h2 className="font-serif text-2xl text-cream">Branding &amp; Homepage Imagery</h2>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-cream/50">
          Update your store logo and the homepage photographs — no code changes needed. Uploads go
          live the moment you save.
        </p>
        <div className="mt-5 space-y-3">
          <ImageSlot
            label="Store Logo"
            hint="Shown in the top-left header. Transparent PNG works best."
            value={logoUrl}
            slotKey="logo"
            inputRef={logoRef}
            testid="settings-logo-upload"
          />
          <ImageSlot
            label="Homepage Hero Image"
            hint="The large pendant photograph beside the headline."
            value={heroImg}
            slotKey="hero"
            inputRef={heroRef}
            testid="settings-hero-upload"
          />
          <ImageSlot
            label="Manifesto Image"
            hint="The editorial model photograph in the dark manifesto section."
            value={manifestoImg}
            slotKey="manifesto"
            inputRef={manifestoRef}
            testid="settings-manifesto-upload"
          />
        </div>
      </div>

      <div className="border border-cream/10 bg-espresso-surface p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <Truck size={18} strokeWidth={1.5} className="text-gold" />
          <h2 className="font-serif text-2xl text-cream">Shipping Charges</h2>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-cream/50">
          A flat rate added to every order, with free shipping above an order amount of your
          choice. Shown in checkout and in every order email.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="ship-flat"
              className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50"
            >
              Flat rate (₹)
            </label>
            <input
              id="ship-flat"
              data-testid="settings-shipping-flat-input"
              type="number"
              min="0"
              value={shipFlat}
              onChange={(e) => setShipFlat(e.target.value)}
              placeholder="0 = always free"
              className="w-full border border-cream/15 bg-transparent px-4 py-3 text-sm text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-gold"
            />
          </div>
          <div>
            <label
              htmlFor="ship-free"
              className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50"
            >
              Free above (₹)
            </label>
            <input
              id="ship-free"
              data-testid="settings-shipping-free-input"
              type="number"
              min="0"
              value={shipFree}
              onChange={(e) => setShipFree(e.target.value)}
              placeholder="Empty = never free"
              className="w-full border border-cream/15 bg-transparent px-4 py-3 text-sm text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-gold"
            />
          </div>
        </div>
      </div>

      <div className="border border-cream/10 bg-espresso-surface p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <Mail size={18} strokeWidth={1.5} className="text-gold" />
          <h2 className="font-serif text-2xl text-cream">Business Email</h2>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-cream/50">
          Order confirmations and payment receipts are sent to customers with this address as
          reply-to — their replies land straight in your inbox.
        </p>
        <input
          data-testid="settings-contact-email-input"
          type="email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          placeholder="you@yourbusiness.com"
          className="mt-5 w-full border border-cream/15 bg-transparent px-4 py-3 text-sm text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-gold"
        />
      </div>

      <div className="border border-cream/10 bg-espresso-surface p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <Bell size={18} strokeWidth={1.5} className="text-gold" />
          <h2 className="font-serif text-2xl text-cream">Low Stock Alerts</h2>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-cream/50">
          When a product's stock quantity falls below this number — after a sale or an edit — an
          alert email is sent to your admin inbox. Leave empty to disable alerts.
        </p>
        <div className="mt-5 flex items-center gap-3">
          <input
            data-testid="settings-threshold-input"
            type="number"
            min="0"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            placeholder="e.g. 3"
            className="w-40 border border-cream/15 bg-transparent px-4 py-3 text-sm text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-gold"
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
            pieces remaining
          </span>
        </div>
      </div>

      <button
        data-testid="settings-save-button"
        onClick={save}
        disabled={saving}
        className="w-full bg-gold py-3.5 font-mono text-[10px] uppercase tracking-[0.25em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98] disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
};

export default SettingsTab;
